import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './AdminActivityLogs.css';

function AdminActivityLogs() {
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0
    });

    // Filters
    const [filters, setFilters] = useState({
        action: 'all',
        fromDate: '',
        toDate: '',
        search: '',
        loginType: 'all'
    });

    const fetchStats = useCallback(async () => {
        try {
            const response = await api.get('/logs/stats');
            setStats(response.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    }, []);

    const fetchLogs = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const params = {
                page,
                limit: pagination.limit,
                action: filters.action !== 'all' ? filters.action : undefined,
                from_date: filters.fromDate || undefined,
                to_date: filters.toDate || undefined,
                search: filters.search || undefined,
                login_type: filters.loginType !== 'all' ? filters.loginType : undefined
            };

            const response = await api.get('/logs/activity', { params });
            setLogs(response.data.logs);
            setPagination(response.data.pagination);
        } catch (err) {
            console.error('Error fetching logs:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.limit]);

    useEffect(() => {
        fetchStats();
        fetchLogs();
    }, [fetchStats, fetchLogs]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchLogs(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchLogs(newPage);
        }
    };

    const getActionBadge = (action) => {
        const badges = {
            login_success: { class: 'success', text: 'เข้าสู่ระบบ' },
            login_failed: { class: 'danger', text: 'ล้มเหลว' },
            logout: { class: 'secondary', text: 'ออกจากระบบ' },
            request_created: { class: 'info', text: 'สร้างคำร้อง' },
            request_cancelled: { class: 'warning', text: 'ยกเลิกคำร้อง' },
            request_approved: { class: 'success', text: 'อนุมัติคำร้อง' },
            request_rejected: { class: 'danger', text: 'ปฏิเสธคำร้อง' },
            request_completed: { class: 'primary', text: 'เสร็จสิ้น' },
            share_slip: { class: 'info', text: 'แชร์สลิป' }
        };
        return badges[action] || { class: 'secondary', text: action };
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';

        // Backend ส่งเวลามาเป็น Thailand timezone แล้ว
        // แปลง string โดยตรง ไม่ผ่าน Date object เพื่อไม่ให้ browser แปลง timezone

        // Format: "2025-12-31T16:57:26.000Z" หรือ "2025-12-31 16:57:26"
        const dateStr = dateString.replace('T', ' ').replace('Z', '').split('.')[0];
        const [datePart, timePart] = dateStr.split(' ');
        const [year, month, day] = datePart.split('-');
        const [hours, minutes, seconds] = timePart.split(':');

        const months = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
        const monthThai = months[parseInt(month)];
        const yearBuddhist = parseInt(year) + 543;

        return `${day} ${monthThai} ${yearBuddhist} ${hours}:${minutes}:${seconds}`;
    };

    const parseUserAgent = (ua) => {
        if (!ua) return { browser: '-', os: '-' };

        let browser = 'Unknown';
        let os = 'Unknown';

        // Detect browser
        if (ua.includes('Firefox')) browser = 'Firefox';
        else if (ua.includes('Edg')) browser = 'Edge';
        else if (ua.includes('Chrome')) browser = 'Chrome';
        else if (ua.includes('Safari')) browser = 'Safari';
        else if (ua.includes('Opera')) browser = 'Opera';

        // Detect OS
        if (ua.includes('Windows')) os = 'Windows';
        else if (ua.includes('Mac')) os = 'macOS';
        else if (ua.includes('Linux')) os = 'Linux';
        else if (ua.includes('Android')) os = 'Android';
        else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

        return { browser, os };
    };

    // ฟังก์ชันดูรายละเอียดผู้ใช้
    const handleViewUser = (log) => {
        if (log.user_id) {
            navigate(`/admin/user-view/${log.user_id}`);
        }
    };

    return (
        <div className="admin-activity-logs">
            <div className="page-header">
                <h1>Activity Logs</h1>
                <p>ประวัติการใช้งานระบบ</p>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon login">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats.today_logins}</span>
                            <span className="stat-label">เข้าสู่ระบบวันนี้</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon failed">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats.today_failed_logins}</span>
                            <span className="stat-label">Login ล้มเหลววันนี้</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon request">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats.today_requests}</span>
                            <span className="stat-label">คำร้องวันนี้</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon users">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="9" cy="7" r="4"/>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats.week_unique_users}</span>
                            <span className="stat-label">ผู้ใช้ 7 วันล่าสุด</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="filters-section">
                <form onSubmit={handleSearch} className="filters-form">
                    <div className="filter-group">
                        <label>ประเภท</label>
                        <select name="action" value={filters.action} onChange={handleFilterChange}>
                            <option value="all">ทั้งหมด</option>
                            <option value="login">Login/Logout</option>
                            <option value="request">คำร้อง</option>
                            <option value="login_success">เข้าสู่ระบบสำเร็จ</option>
                            <option value="login_failed">เข้าสู่ระบบล้มเหลว</option>
                            <option value="logout">ออกจากระบบ</option>
                            <option value="share_slip">แชร์สลิป</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>ประเภท Login</label>
                        <select name="loginType" value={filters.loginType} onChange={handleFilterChange}>
                            <option value="all">ทั้งหมด</option>
                            <option value="cid">CID</option>
                            <option value="username">Username</option>
                            <option value="google">Google</option>
                            <option value="providerid">Provider ID</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>จากวันที่</label>
                        <input
                            type="date"
                            name="fromDate"
                            value={filters.fromDate}
                            onChange={handleFilterChange}
                        />
                    </div>
                    <div className="filter-group">
                        <label>ถึงวันที่</label>
                        <input
                            type="date"
                            name="toDate"
                            value={filters.toDate}
                            onChange={handleFilterChange}
                        />
                    </div>
                    <div className="filter-group search-group">
                        <label>ค้นหา</label>
                        <input
                            type="text"
                            name="search"
                            placeholder="ชื่อ หรือ CID..."
                            value={filters.search}
                            onChange={handleFilterChange}
                        />
                    </div>
                    <button type="submit" className="btn-search">
                        ค้นหา
                    </button>
                </form>
            </div>

            {/* Logs Table */}
            <div className="logs-table-container">
                {loading ? (
                    <div className="loading">กำลังโหลด...</div>
                ) : logs.length === 0 ? (
                    <div className="no-data">ไม่พบข้อมูล</div>
                ) : (
                    <>
                        <table className="logs-table">
                            <thead>
                                <tr>
                                    <th>เวลา</th>
                                    <th>ผู้ใช้</th>
                                    <th>Action</th>
                                    <th>รายละเอียด</th>
                                    <th>IP Address</th>
                                    <th>Browser/OS</th>
                                    <th style={{ width: '80px', textAlign: 'center' }}>ดูข้อมูล</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => {
                                    const badge = getActionBadge(log.action);
                                    const { browser, os } = parseUserAgent(log.user_agent);
                                    return (
                                        <tr key={log.id}>
                                            <td className="td-time">
                                                {formatDate(log.created_at)}
                                            </td>
                                            <td className="td-user">
                                                <div className="user-info">
                                                    <span className="user-name">{log.fullname || '-'}</span>
                                                    <span className="user-id">{log.username || '-'}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge badge-${badge.class}`}>
                                                    {badge.text}
                                                </span>
                                            </td>
                                            <td className="td-detail">
                                                {log.action_detail || '-'}
                                            </td>
                                            <td className="td-ip">
                                                {log.ip_address || '-'}
                                            </td>
                                            <td className="td-browser">
                                                <span>{browser}</span>
                                                <span className="os-name">{os}</span>
                                            </td>
                                            <td className="td-action">
                                                {log.user_id && (
                                                    <button
                                                        className="btn-view-user"
                                                        onClick={() => handleViewUser(log)}
                                                        title="ดูข้อมูลผู้ใช้"
                                                    >
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="18" height="18">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                        ดูข้อมูล
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <div className="pagination">
                            <div className="pagination-info">
                                แสดง {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} จาก {pagination.total} รายการ
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(1)}
                                    disabled={pagination.page === 1}
                                >
                                    หน้าแรก
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                >
                                    ก่อนหน้า
                                </button>
                                <span className="page-number">
                                    หน้า {pagination.page} / {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.totalPages}
                                >
                                    ถัดไป
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                    disabled={pagination.page === pagination.totalPages}
                                >
                                    หน้าสุดท้าย
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminActivityLogs;
